import { useState, useEffect } from "react"
import api from "../services/api"
import { Helmet } from "react-helmet"
import SendEmailSkeleton from "./skeleton/SendEmailSkeleton"
import Swal from "sweetalert2"

export default function SendEmail() {
  const [units, setUnits] = useState([])
  const [error, setError] = useState(null)
  const [form, setForm] = useState({
    to: [],
    subject: "",
    tenantName: "",
    message: "",
    templateType: "announcement",
    date: "",
    time: "",
    amount: "",
    dueDate: "",
  })

  const [pageLoading, setPageLoading] = useState(true)
  const [sending, setSending] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target

    // when tenant is selected
    if (name === "tenant") {
      const selectedTenant = units.find((unit) => unit._id === value)

      setForm((prev) => ({
        ...prev,
        tenantName: selectedTenant?.name || "",
      }))

      return
    }

    // normal inputs
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }))
  }
  const sendEmail = async (e) => {
    e.preventDefault()
    setSending(true)

    try {
      await api.post(`/api/emails/send-email`, form)

      await Swal.fire({
        icon: "success",
        text: "Email sent successfully",
        confirmButtonColor: "#f97316",
        timer: 1500,
        showConfirmButton: false,
      })

      setForm({
        to: [],
        subject: "",
        tenantName: "",
        message: "",
      })
    } catch (error) {
      Swal.fire({
        icon: "error",
        text: "Failed to send email",
      })
    } finally {
      setSending(false)
    }
  }

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await api.get(`/api/tenants`)
        setUnits(res.data)
      } catch {
        setError("Failed to load units")
      } finally {
        setPageLoading(false)
      }
    }

    fetchUnits()
  }, [])

  return (
    <>
      <Helmet>
        <title>Send Email - Stay Suite | Apartment Management System</title>
      </Helmet>
      <div className="w-full max-w-md mx-auto mt-10">
        {pageLoading ? (
          <SendEmailSkeleton />
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-fadeIn">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">
              Send Email
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Send message directly to tenant or user
            </p>

            <form onSubmit={sendEmail} className="space-y-4">
              {/* TO */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Recipient Email
                </label>
                <input
                  type="text"
                  name="to"
                  value={form.to.join(", ")}
                  onChange={(e) => {
                    const emails = e.target.value
                      .split(",")
                      .map((e) => e.trim())
                      .filter(Boolean)

                    setForm((prev) => ({
                      ...prev,
                      to: emails,
                    }))
                  }}
                  placeholder="email1@gmail.com, email2@gmail.com"
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              {/* TEMPLATE TYPE */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Email Template
                </label>

                <select
                  name="templateType"
                  value={form.templateType}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="announcement">Announcement</option>
                  <option value="maintenance">Maintenance Notice</option>
                  <option value="paymentReminder">Payment Reminder</option>
                </select>
              </div>

              {/* SUBJECT */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Tenant
                </label>

                <select
                  multiple
                  name="tenant"
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions)

                    const emails = selected.map((opt) => opt.dataset.email)
                    const names = selected.map((opt) => opt.text)

                    setForm((prev) => ({
                      ...prev,
                      to: emails,
                      tenantName: names.join(", "),
                    }))
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  {units.map((unit) => (
                    <option
                      key={unit._id}
                      value={unit._id}
                      data-email={unit.email}
                    >
                      {unit.name}
                    </option>
                  ))}
                </select>

                <label className="text-sm font-medium text-gray-700">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder="Email subject"
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                         focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* MESSAGE */}
              {form.templateType === "announcement" && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">
                    Message
                  </label>

                  <textarea
                    name="message"
                    rows="5"
                    value={form.message}
                    onChange={handleChange}
                    maxLength={500}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm resize-none
        focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Message"
                  />

                  <p className="text-xs text-gray-500 text-right">
                    {form.message.length} / 500
                  </p>
                </div>
              )}

              {form.templateType === "maintenance" && (
                <>
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />

                  <input
                    type="time"
                    name="time"
                    value={form.time}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </>
              )}

              {form.templateType === "paymentReminder" && (
                <>
                  <input
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder="Amount"
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />

                  <input
                    type="date"
                    name="dueDate"
                    value={form.dueDate}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </>
              )}

              {/* ACTIONS */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={sending}
                  className="px-5 py-2 rounded-lg text-sm font-semibold
                         bg-orange-600 text-white
                         hover:bg-orange-700
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition"
                >
                  {sending ? "Sending..." : "Send Email"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  )
}
